import React, { useEffect, useState } from 'react';
import styles from './RescheduleAppointmentModal.module.css';
import Button from '../../../components/Button/Button';
import TimePicker from '../../../components/TimePicker/TimePicker';
import Calendar from '../../../components/Calendar/Calendar';

interface RescheduleAppointmentModalProps {
    isOpen: boolean;
    appointmentId: number;
    customerName?: string;
    onClose: () => void;
    onConfirmReschedule: (id: number, newDateTimeIso: string) => Promise<void>;
}

const RescheduleAppointmentModal: React.FC<RescheduleAppointmentModalProps> = ({
    isOpen,
    appointmentId,
    customerName,
    onClose,
    onConfirmReschedule,
}) => {
    const [rescheduleDate, setRescheduleDate] = useState('');
    const [rescheduleDateObj, setRescheduleDateObj] = useState<Date | null>(null);
    const [rescheduleTime, setRescheduleTime] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationError, setValidationError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setRescheduleDate('');
            setRescheduleDateObj(null);
            setRescheduleTime('');
            setValidationError('');
            setIsSubmitting(false);
        }
    }, [isOpen]);

    const convertToIso = (dateStr: string, timeAmPm: string): string => {
        const [time, period] = timeAmPm.split(' ');
        const [hStr, mStr] = time.split(':');
        let hours = parseInt(hStr, 10);
        const minutes = parseInt(mStr, 10);
        if (period === 'PM' && hours !== 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;
        const date = new Date(dateStr);
        date.setHours(hours, minutes, 0, 0);
        return date.toISOString();
    };

    const handleConfirm = async () => {
        if (!rescheduleDate || !rescheduleTime) {
            setValidationError('Please select both date and time.');
            return;
        }
        try {
            setIsSubmitting(true);
            const iso = convertToIso(rescheduleDate, rescheduleTime);
            await onConfirmReschedule(appointmentId, iso);
            onClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.currentTarget === e.target && !isSubmitting) {
            onClose();
        }
    };

    return (
        <div className={`${styles.modalBackground} ${isOpen ? styles.open : ''}`} onClick={handleOverlayClick}>
            <div className={`${styles.modalContainer} ${isOpen ? styles.open : ''}`}>
                {isSubmitting && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.spinner}>
                            <span>Rescheduling...</span>
                        </div>
                    </div>
                )}

                <div className={styles.headerCloseContainer}>
                    <button className={styles.closeButton} onClick={onClose} disabled={isSubmitting} aria-label="Close modal">
                        &times;
                    </button>
                    <h2 className={styles.header}>Reschedule appointment</h2>
                </div>

                <div className={styles.appointmentForm}>
                    {customerName && (
                        <p className={styles.subHeader}>Select a new date and time for {customerName}'s appointment</p>
                    )}

                    <Calendar
                        label="Appointment Date"
                        selectedDate={rescheduleDateObj || undefined}
                        onDateChange={(d) => {
                            setRescheduleDateObj(d);
                            const y = d.getFullYear();
                            const m = String(d.getMonth() + 1).padStart(2, '0');
                            const day = String(d.getDate()).padStart(2, '0');
                            setRescheduleDate(`${y}-${m}-${day}`);
                            setValidationError('');
                        }}
                        value={rescheduleDateObj ? 'dateselected' : 'Select appointment date'}
                        leftAligned={false}
                        noBorderRadius={true}
                    />

                    <TimePicker
                        label="Appointment Time"
                        value={rescheduleTime || 'Select appointment time'}
                        selectedTime={rescheduleTime}
                        onTimeChange={(t) => { setRescheduleTime(t); setValidationError(''); }}
                        leftAligned={false}
                        noBorderRadius={true}
                    />

                    {validationError && (
                        <p className={styles.validationMessage}>{validationError}</p>
                    )}

                    <div className={styles.buttonContainer}>
                        <Button
                            label="Cancel"
                            onClick={onClose}
                            noAppearance={true}
                            size="medium"
                            disabled={isSubmitting}
                        />
                        <Button
                            label={isSubmitting ? 'Rescheduling...' : 'Confirm Reschedule'}
                            onClick={handleConfirm}
                            variant="primary"
                            size="medium"
                            disabled={isSubmitting || !rescheduleDate || !rescheduleTime}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RescheduleAppointmentModal;
